import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, Plus, Download, CheckCircle, XCircle, RefreshCw, Settings, AlertTriangle, Receipt, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLORS = { draft: 'bg-gray-100 text-gray-600', stamped: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-red-100 text-red-700' };
const STATUS_LABEL = { draft: 'Borrador', stamped: 'Timbrado', cancelled: 'Cancelado' };

const REGIMENES = [
  { value: '601', label: '601 - General de Ley Personas Morales' },
  { value: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
  { value: '605', label: '605 - Sueldos y Salarios e Ingresos Asimilados' },
  { value: '612', label: '612 - Personas Físicas con Actividades Empresariales' },
  { value: '621', label: '621 - Incorporación Fiscal' },
  { value: '625', label: '625 - Régimen de las Actividades Empresariales con Ingresos a través de Plataformas Tecnológicas' },
  { value: '626', label: '626 - Régimen Simplificado de Confianza' },
];

const USOS_CFDI = [
  { value: 'G01', label: 'G01 - Adquisición de mercancias' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
  { value: 'S01', label: 'S01 - Sin efectos fiscales' },
  { value: 'CP01', label: 'CP01 - Pagos' },
];

const EMPTY_INVOICE = { receptor_rfc: '', receptor_nombre: '', receptor_regimen: '626', uso_cfdi: 'G03', total: '', subtotal: '', iva: '', description: '', payment_method: 'PUE', payment_form: '03', notes: '' };

export default function CFDIPage() {
  const [config, setConfig] = useState({ enabled: false, rfc: '', razon_social: '', regimen_fiscal: '601', cp: '', provider: 'facturapi' });
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState(EMPTY_INVOICE);
  const [saving, setSaving] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, invRes] = await Promise.all([
        fetch(`${API_URL}/api/cfdi/config`, { headers: authHeader() }),
        fetch(`${API_URL}/api/cfdi/invoices`, { headers: authHeader() }),
      ]);
      if (cfgRes.ok) setConfig(await cfgRes.json());
      if (invRes.ok) setInvoices(await invRes.json());
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await fetch(`${API_URL}/api/cfdi/config`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(config) });
      if (res.ok) { toast.success('Configuración fiscal guardada'); load(); }
    } catch { toast.error('Error'); }
    finally { setSavingConfig(false); }
  };

  const createInvoice = async () => {
    if (!invoiceForm.receptor_rfc || !invoiceForm.total) { toast.error('RFC y total son obligatorios'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cfdi/invoices`, { method: 'POST', headers: authHeader(), body: JSON.stringify(invoiceForm) });
      if (res.ok) { toast.success('Factura creada'); setShowInvoiceModal(false); load(); }
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const stampInvoice = async (id) => {
    const res = await fetch(`${API_URL}/api/cfdi/invoices/${id}/stamp`, { method: 'PUT', headers: authHeader() });
    if (res.ok) { toast.success('Factura timbrada correctamente'); load(); }
    else toast.error('Error al timbrar');
  };

  const cancelInvoice = async (id) => {
    if (!window.confirm('¿Cancelar esta factura? Esta acción no se puede deshacer.')) return;
    await fetch(`${API_URL}/api/cfdi/invoices/${id}/cancel`, { method: 'PUT', headers: authHeader() });
    toast.success('Factura cancelada'); load();
  };

  const calculateIVA = (total) => {
    const t = parseFloat(total) || 0;
    const subtotal = Math.round((t / 1.16) * 100) / 100;
    const iva = Math.round((t - subtotal) * 100) / 100;
    setInvoiceForm(p => ({ ...p, total, subtotal: subtotal.toString(), iva: iva.toString() }));
  };

  const stats = [
    { label: 'Total facturas', value: invoices.length, icon: FileText, color: 'text-gray-500' },
    { label: 'Timbradas', value: invoices.filter(i => i.status === 'stamped').length, icon: CheckCircle, color: 'text-emerald-500' },
    { label: 'Borradores', value: invoices.filter(i => i.status === 'draft').length, icon: Receipt, color: 'text-amber-500' },
    { label: 'Canceladas', value: invoices.filter(i => i.status === 'cancelled').length, icon: XCircle, color: 'text-red-500' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Facturación SAT / CFDI" subtitle="Emite comprobantes fiscales digitales (CFDI 4.0) para tus clientes" helpSection="cfdi" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {!config.enabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">Facturación no configurada</p>
              <p className="text-sm text-amber-700">Configura tus datos fiscales en la pestaña "Configuración fiscal" para comenzar a emitir CFDIs.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Card key={i}><CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-8 h-8 ${s.color}`} />
              <div><p className="text-2xl font-black">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </div>

        <Tabs defaultValue="invoices">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <TabsList>
              <TabsTrigger value="invoices">Facturas</TabsTrigger>
              <TabsTrigger value="config">Configuración fiscal</TabsTrigger>
            </TabsList>
            <Button onClick={() => { setInvoiceForm(EMPTY_INVOICE); setShowInvoiceModal(true); }} className="bg-orange-500 hover:bg-orange-600 text-white gap-1" disabled={!config.enabled}>
              <Plus className="w-4 h-4" /> Nueva Factura
            </Button>
          </div>

          <TabsContent value="invoices">
            {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-orange-500 animate-spin" /></div>
            : invoices.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No hay facturas</p>
                <p className="text-sm">Crea tu primera factura CFDI 4.0</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map(inv => (
                  <Card key={inv.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><FileText className="w-5 h-5 text-gray-600" /></div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-gray-900">{inv.folio}</p>
                              <Badge className={STATUS_COLORS[inv.status]}>{STATUS_LABEL[inv.status]}</Badge>
                            </div>
                            <p className="text-sm text-gray-500">{inv.receptor_rfc} · {inv.receptor_nombre || '—'} · <span className="font-semibold text-gray-700">${parseFloat(inv.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></p>
                            {inv.uuid && <p className="text-xs text-gray-400 font-mono">UUID: {inv.uuid}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {inv.status === 'draft' && (
                            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1" onClick={() => stampInvoice(inv.id)}>
                              <CheckCircle className="w-3.5 h-3.5" /> Timbrar
                            </Button>
                          )}
                          {inv.status === 'stamped' && (
                            <>
                              <Button size="sm" variant="outline" className="gap-1"><Download className="w-3.5 h-3.5" /> PDF</Button>
                              <Button size="sm" variant="outline" className="gap-1"><Download className="w-3.5 h-3.5" /> XML</Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => cancelInvoice(inv.id)}><XCircle className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="config">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-orange-500" /> Datos Fiscales del Emisor</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <CheckCircle className={`w-5 h-5 ${config.enabled ? 'text-emerald-500' : 'text-gray-300'}`} />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Facturación habilitada</p>
                    <p className="text-xs text-gray-500">Activa para comenzar a emitir CFDIs</p>
                  </div>
                  <input type="checkbox" checked={config.enabled} onChange={e => setConfig(p => ({ ...p, enabled: e.target.checked }))} className="rounded" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>RFC del emisor *</Label><Input value={config.rfc} onChange={e => setConfig(p => ({ ...p, rfc: e.target.value.toUpperCase() }))} placeholder="XAXX010101000" /></div>
                  <div><Label>Código Postal fiscal *</Label><Input value={config.cp} onChange={e => setConfig(p => ({ ...p, cp: e.target.value }))} placeholder="06600" /></div>
                  <div className="col-span-2"><Label>Razón social *</Label><Input value={config.razon_social} onChange={e => setConfig(p => ({ ...p, razon_social: e.target.value.toUpperCase() }))} placeholder="MI RESTAURANTE SA DE CV" /></div>
                  <div className="col-span-2"><Label>Régimen fiscal</Label>
                    <Select value={config.regimen_fiscal} onValueChange={v => setConfig(p => ({ ...p, regimen_fiscal: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{REGIMENES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Proveedor PAC</Label>
                    <Select value={config.provider} onValueChange={v => setConfig(p => ({ ...p, provider: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="facturapi">FacturAPI</SelectItem>
                        <SelectItem value="finkok">Finkok</SelectItem>
                        <SelectItem value="solucion_factible">Solución Factible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>API Key del PAC</Label><Input type="password" value={config.api_key || ''} onChange={e => setConfig(p => ({ ...p, api_key: e.target.value }))} placeholder="••••••••••••" /></div>
                </div>
                <Button onClick={saveConfig} disabled={savingConfig} className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
                  {savingConfig ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />} Guardar configuración fiscal
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva Factura CFDI 4.0</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>RFC receptor *</Label><Input value={invoiceForm.receptor_rfc} onChange={e => setInvoiceForm(p => ({ ...p, receptor_rfc: e.target.value.toUpperCase() }))} placeholder="XAXX010101000" /></div>
              <div><Label>Régimen fiscal receptor</Label>
                <Select value={invoiceForm.receptor_regimen} onValueChange={v => setInvoiceForm(p => ({ ...p, receptor_regimen: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{REGIMENES.map(r => <SelectItem key={r.value} value={r.value}>{r.value}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Nombre/Razón social del receptor</Label><Input value={invoiceForm.receptor_nombre} onChange={e => setInvoiceForm(p => ({ ...p, receptor_nombre: e.target.value.toUpperCase() }))} /></div>
              <div className="col-span-2"><Label>Uso del CFDI</Label>
                <Select value={invoiceForm.uso_cfdi} onValueChange={v => setInvoiceForm(p => ({ ...p, uso_cfdi: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{USOS_CFDI.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Total (con IVA) *</Label><Input type="number" value={invoiceForm.total} onChange={e => calculateIVA(e.target.value)} placeholder="1160.00" /></div>
              <div><Label>Subtotal (sin IVA)</Label><Input type="number" value={invoiceForm.subtotal} readOnly className="bg-gray-50" /></div>
              <div><Label>IVA (16%)</Label><Input type="number" value={invoiceForm.iva} readOnly className="bg-gray-50" /></div>
              <div><Label>Método de pago</Label>
                <Select value={invoiceForm.payment_method} onValueChange={v => setInvoiceForm(p => ({ ...p, payment_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUE">PUE - Pago en una sola exhibición</SelectItem>
                    <SelectItem value="PPD">PPD - Pago en parcialidades o diferido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Forma de pago</Label>
                <Select value={invoiceForm.payment_form} onValueChange={v => setInvoiceForm(p => ({ ...p, payment_form: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="01">01 - Efectivo</SelectItem>
                    <SelectItem value="02">02 - Cheque nominativo</SelectItem>
                    <SelectItem value="03">03 - Transferencia electrónica</SelectItem>
                    <SelectItem value="04">04 - Tarjeta de crédito</SelectItem>
                    <SelectItem value="28">28 - Tarjeta de débito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Descripción del servicio</Label><Textarea value={invoiceForm.description} onChange={e => setInvoiceForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Servicios de alimentos y bebidas..." /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Cancelar</Button>
            <Button onClick={createInvoice} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Crear Factura'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
